// Composant de recherche client - Source : Commercial Hub
// Utilisé par Operation Hub (Tickets, Missions, Dossiers)
// Règle V1 : Impossible de créer un client depuis Operation

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, User, Building2, Loader2, Plus, ExternalLink } from "lucide-react";
import { Input } from "./input";
import { Button } from "./button";
import { Badge } from "./badge";
import { formatClientFullName } from "../../utils/clientFormat";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export function ClientSearchSelect({ 
  value, 
  onChange, 
  placeholder = "Rechercher un client...",
  disabled = false,
  className = ""
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Charger le client sélectionné si value existe
  useEffect(() => {
    if (value && !selectedClient) {
      axios.get(`${API}/api/commercial/clients/${value}`, { headers: getAuthHeaders() })
        .then(res => setSelectedClient(res.data))
        .catch(() => setSelectedClient(null));
    } else if (!value) {
      setSelectedClient(null);
    }
  }, [value, selectedClient]);

  // Recherche avec debounce
  const searchClients = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/commercial/clients/search?q=${encodeURIComponent(searchQuery)}&limit=10`,
        { headers: getAuthHeaders() }
      );
      setResults(res.data || []);
    } catch (err) {
      console.error("Erreur recherche clients:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 2) {
        searchClients(query);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchClients]);

  const handleSelect = (client) => {
    setSelectedClient(client);
    onChange(client.id);
    setQuery("");
    setShowDropdown(false);
    setResults([]);
  };

  const handleClear = () => {
    setSelectedClient(null);
    onChange(null);
    setQuery("");
  };

  const openCommercialHub = () => {
    // Ouvre le Commercial Hub dans un nouvel onglet
    window.open("/#/commercial/clients", "_blank");
  };

  return (
    <div className={`relative ${className}`}>
      {/* Client sélectionné */}
      {selectedClient ? (
        <div className="flex items-center gap-2 p-2 bg-[var(--oh-bg-elevated)] border border-white/10 rounded-md">
          <div className="flex-1 flex items-center gap-2">
            {selectedClient.societe ? (
              <Building2 className="w-4 h-4 text-[var(--oh-info-text)]" />
            ) : (
              <User className="w-4 h-4 text-[var(--oh-accent-text)]" />
            )}
            <span className="text-[var(--oh-text)] font-medium">
              {selectedClient.display_name || formatClientFullName(selectedClient)}
            </span>
            {selectedClient.type_client && (
              <Badge variant="outline" className="text-xs text-[var(--oh-text-secondary)] border-[var(--oh-border)]">
                {selectedClient.type_client}
              </Badge>
            )}
          </div>
          <Button 
            type="button"
            variant="ghost" 
            size="sm" 
            onClick={handleClear}
            disabled={disabled}
            className="text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)] h-6 px-2"
          >
            ✕
          </Button>
        </div>
      ) : (
        /* Champ de recherche */
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-muted)]" />
          <Input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder={placeholder}
            disabled={disabled}
            className="pl-10 bg-[var(--oh-bg-elevated)] border-white/10 text-[var(--oh-text)]"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-muted)] animate-spin" />
          )}
        </div>
      )}

      {/* Dropdown résultats */}
      {showDropdown && !selectedClient && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--oh-bg-surface)] border border-white/10 rounded-md shadow-lg max-h-64 overflow-auto">
          {query.length < 2 ? (
            <div className="p-3 text-[var(--oh-text-muted)] text-sm text-center">
              Tapez au moins 2 caractères...
            </div>
          ) : loading ? (
            <div className="p-3 text-[var(--oh-text-muted)] text-sm text-center flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Recherche...
            </div>
          ) : results.length === 0 ? (
            <div className="p-3 text-center">
              <p className="text-[var(--oh-text-muted)] text-sm mb-2">Aucun client trouvé</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openCommercialHub}
                className="text-[var(--oh-accent-text)] border-[var(--oh-accent)] hover:bg-[var(--oh-accent-bg)]"
              >
                <Plus className="w-3 h-3 mr-1" />
                Créer dans Commercial Hub
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </div>
          ) : (
            results.map((client) => (
              <button
                key={client.id}
                type="button"
                onClick={() => handleSelect(client)}
                className="w-full px-3 py-2 text-left hover:bg-[var(--oh-bg-elevated)] flex items-center gap-2 transition-colors"
              >
                {client.societe ? (
                  <Building2 className="w-4 h-4 text-[var(--oh-info-text)] flex-shrink-0" />
                ) : (
                  <User className="w-4 h-4 text-[var(--oh-accent-text)] flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[var(--oh-text)] font-medium truncate">
                    {client.display_name || `${client.prenom || ""} ${client.nom}`.trim()}
                  </div>
                  <div className="text-xs text-[var(--oh-text-muted)] truncate">
                    {[client.email, client.telephone, client.ville].filter(Boolean).join(" • ")}
                  </div>
                </div>
                <Badge variant="outline" className="text-xs text-[var(--oh-text-muted)] border-[var(--oh-border)] flex-shrink-0">
                  {client.type_client}
                </Badge>
              </button>
            ))
          )}
          
          {/* Lien vers Commercial Hub */}
          {results.length > 0 && (
            <div className="border-t border-white/10 p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={openCommercialHub}
                className="w-full text-[var(--oh-text-muted)] hover:text-[var(--oh-text)] text-xs"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Gérer les clients dans Commercial Hub
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Overlay pour fermer le dropdown */}
      {showDropdown && !selectedClient && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
}

export default ClientSearchSelect;
